import React, { useState, useEffect } from "react";
import api from "../services/api";
import "./AddUserPage.css";

const AddUserPage = () => {
  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    role: "",
    password: "",
  });

  const [businesses, setBusinesses] = useState([]);
  const [selectedBizIds, setSelectedBizIds] = useState([]);

  // List section
  const [businessUsers, setBusinessUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [usersError, setUsersError] = useState(null);
  const [filterBizId, setFilterBizId] = useState("");

  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Load businesses for authenticated owner
  useEffect(() => {
    const loadBusinesses = async () => {
      try {
        const res = await api.get("/businesses");
        setBusinesses(res.data || []);
      } catch {
        setError("Failed to load businesses.");
      }
    };
    loadBusinesses();
  }, []);

  // Fetch business users (optional filter)
  const fetchBusinessUsers = async (bizId = "") => {
    setUsersLoading(true);
    setUsersError(null);
    try {
      const res = await api.get("/business-users", {
        params: bizId ? { business_id: bizId } : {},
      });
      const rows = (res.data || []).map((r) => ({
        ...r,
        business_names: Array.isArray(r.business_names)
          ? r.business_names
          : (r.business_names || "")
              .split(",")
              .map((s) => s.trim())
              .filter(Boolean),
      }));
      setBusinessUsers(rows);
    } catch {
      setUsersError("Failed to load business users.");
    } finally {
      setUsersLoading(false);
    }
  };

  useEffect(() => {
    fetchBusinessUsers();
  }, []);

  useEffect(() => {
    fetchBusinessUsers(filterBizId);
  }, [filterBizId]);

  const handleChange = (e) => {
    setFormData((p) => ({ ...p, [e.target.name]: e.target.value }));
  };

  const toggleBiz = (id) => {
    setSelectedBizIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    const { first_name, last_name, email, phone, role, password } = formData;
    if (!first_name || !last_name || !email || !phone || !role || !password) {
      return setError("All fields are required.");
    }
    if (selectedBizIds.length === 0) {
      return setError("Select at least one business.");
    }
    if (role === "manager" && selectedBizIds.length !== 1) {
      return setError("Managers can be assigned to exactly one business.");
    }

    try {
      const payload = {
        first_name,
        last_name,
        email: email.toLowerCase(),
        phone,
        password,
        role, // 'admin' | 'hr' | 'manager' | 'accountant'
        business_ids: selectedBizIds,
      };

      const response = await api.post("/business-user-add-bulk", payload);
      setSuccess(response?.data || "Business user created and linked.");

      setFormData({
        first_name: "",
        last_name: "",
        email: "",
        phone: "",
        role: "",
        password: "",
      });
      setSelectedBizIds([]);

      fetchBusinessUsers(filterBizId);
    } catch (err) {
      setError(err?.response?.data || "Failed to add business user.");
    }
  };

  return (
    <div className="adduser-container">
      <h2 className="adduser-title">Add New User</h2>

      {success && <p className="adduser-success">{success}</p>}
      {error && <p className="adduser-error">{error}</p>}

      <form onSubmit={handleSubmit} className="adduser-form" autoComplete="off">
        <label>First Name:</label>
        <input
          type="text"
          name="first_name"
          value={formData.first_name}
          onChange={handleChange}
          required
        />

        <label>Last Name:</label>
        <input
          type="text"
          name="last_name"
          value={formData.last_name}
          onChange={handleChange}
          required
        />

        <label>Email:</label>
        <input
          type="email"
          name="email"
          value={formData.email}
          onChange={handleChange}
          required
        />

        <label>Phone:</label>
        <input
          type="tel"
          name="phone"
          value={formData.phone}
          onChange={handleChange}
          required
        />

        <label>Role:</label>
        <select name="role" value={formData.role} onChange={handleChange} required>
          <option value="">Select Role</option>
          <option value="admin">Admin</option>
          <option value="hr">HR</option>
          <option value="manager">Manager</option>
          <option value="accountant">Accountant</option>
        </select>

        <label>Password:</label>
        <input
          type="password"
          name="password"
          value={formData.password}
          onChange={handleChange}
          required
          minLength={8}
        />

        <label>Businesses:</label>
        <div className="adduser-bizlist">
          {businesses.map((biz) => (
            <label key={biz.id} className="adduser-bizitem">
              <input
                type="checkbox"
                checked={selectedBizIds.includes(biz.id)}
                onChange={() => toggleBiz(biz.id)}
              />
              <span>{biz.name}</span>
            </label>
          ))}
          {businesses.length === 0 && (
            <div className="adduser-muted">No businesses found.</div>
          )}
        </div>

        <button type="submit" className="adduser-submit">Add User</button>
      </form>

      <div className="adduser-listwrap">
        <h3>Existing Business Users</h3>

        <div className="adduser-filter">
          <label>Filter by Business:</label>
          <select
            value={filterBizId}
            onChange={(e) => setFilterBizId(e.target.value)}
          >
            <option value="">All</option>
            {businesses.map((b) => (
              <option key={b.id} value={b.id}>
                {b.name}
              </option>
            ))}
          </select>
          <button type="button" onClick={() => fetchBusinessUsers(filterBizId)}>
            Refresh
          </button>
        </div>

        {usersError && <p className="adduser-error">{usersError}</p>}
        {usersLoading && <p className="adduser-muted">Loading...</p>}

        {!usersLoading && businessUsers.length === 0 && (
          <p className="adduser-muted">No business users found.</p>
        )}

        <div className="adduser-userlist">
          {businessUsers.map((u) => (
            <div key={u.id} className="adduser-usercard">
              <div className="adduser-username">
                {u.first_name} {u.last_name}
              </div>
              <div className="adduser-userline">{u.email}</div>
              <div className="adduser-userline">{u.phone}</div>
              <div className="adduser-userline">Role: {u.role}</div>
              <div className="adduser-userline adduser-small">
                {u.business_names && u.business_names.length
                  ? `Businesses: ${u.business_names.join(", ")}`
                  : "Businesses: -"}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default AddUserPage;




// import React, { useState, useEffect } from "react";
// import api from "../services/api"; // Your API service

// const AddUserPage = () => {
//   const [formData, setFormData] = useState({
//     first_name: "",
//     last_name: "",
//     email: "",
//     phone: "",
//     role: "",
//     password: "",
//   });

//   const [businesses, setBusinesses] = useState([]);
//   const [selectedBizIds, setSelectedBizIds] = useState([]);

//   // NEW: state for listing business users
//   const [businessUsers, setBusinessUsers] = useState([]);
//   const [usersLoading, setUsersLoading] = useState(false);
//   const [usersError, setUsersError] = useState(null);
//   const [filterBizId, setFilterBizId] = useState(""); // "" = All

//   const [error, setError] = useState(null);
//   const [success, setSuccess] = useState(null);

//   // Load businesses for the authenticated owner (you already have this endpoint)
//   useEffect(() => {
//     const loadBusinesses = async () => {
//       try {
//         const res = await api.get("/businesses");
//         setBusinesses(res.data || []);
//       } catch {
//         setError("Failed to load businesses.");
//       }
//     };
//     loadBusinesses();
//   }, []);

//   // Fetch business users (optionally filtered by business)
//   const fetchBusinessUsers = async (bizId = "") => {
//     setUsersLoading(true);
//     setUsersError(null);
//     try {
//       const res = await api.get("/business-users", {
//         params: bizId ? { business_id: bizId } : {},
//       });
//       // Backend returns arrays; but if you ever return CSV strings, normalize here:
//       const rows = (res.data || []).map((r) => ({
//         ...r,
//         business_names: Array.isArray(r.business_names)
//           ? r.business_names
//           : (r.business_names || "")
//               .split(",")
//               .map((s) => s.trim())
//               .filter(Boolean),
//       }));
//       setBusinessUsers(rows);
//     } catch (e) {
//       setUsersError("Failed to load business users.");
//     } finally {
//       setUsersLoading(false);
//     }
//   };

//   // Initial load of business users
//   useEffect(() => {
//     fetchBusinessUsers();
//   }, []);

//   // Refetch when filter changes
//   useEffect(() => {
//     fetchBusinessUsers(filterBizId);
//   }, [filterBizId]);

//   const handleChange = (e) => {
//     setFormData({ ...formData, [e.target.name]: e.target.value });
//   };

//   const toggleBiz = (id) => {
//     setSelectedBizIds((prev) =>
//       prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
//     );
//   };

//   const handleSubmit = async (e) => {
//     e.preventDefault();
//     setError(null);
//     setSuccess(null);

//     const { first_name, last_name, email, phone, role, password } = formData;
//     if (!first_name || !last_name || !email || !phone || !role || !password) {
//       return setError("All fields are required.");
//     }
//     if (selectedBizIds.length === 0) {
//       return setError("Select at least one business.");
//     }
//     if (role === "manager" && selectedBizIds.length !== 1) {
//       return setError("Managers can be assigned to exactly one business.");
//     }

//     try {
//       const payload = {
//         first_name,
//         last_name,
//         email: email.toLowerCase(),
//         phone,
//         password,
//         role, // 'admin' | 'hr' | 'manager' | 'accountant'
//         business_ids: selectedBizIds,
//       };

//       const response = await api.post("/business-user-add-bulk", payload);
//       setSuccess(response?.data || "Business user created and linked.");

//       // Reset
//       setFormData({
//         first_name: "",
//         last_name: "",
//         email: "",
//         phone: "",
//         role: "",
//         password: "",
//       });
//       setSelectedBizIds([]);

//       // Refresh the list so the new user shows up
//       fetchBusinessUsers(filterBizId);
//     } catch (err) {
//       setError(err?.response?.data || "Failed to add business user.");
//     }
//   };

//   return (
//     <div style={styles.container}>
//       <h2>Add New User</h2>

//       {success && <p style={styles.success}>{success}</p>}
//       {error && <p style={styles.error}>{error}</p>}

//       <form onSubmit={handleSubmit} style={styles.form}>
//         <label>First Name:</label>
//         <input
//           type="text"
//           name="first_name"
//           value={formData.first_name}
//           onChange={handleChange}
//           required
//         />

//         <label>Last Name:</label>
//         <input
//           type="text"
//           name="last_name"
//           value={formData.last_name}
//           onChange={handleChange}
//           required
//         />

//         <label>Email:</label>
//         <input
//           type="email"
//           name="email"
//           value={formData.email}
//           onChange={handleChange}
//           required
//         />

//         <label>Phone:</label>
//         <input
//           type="tel"
//           name="phone"
//           value={formData.phone}
//           onChange={handleChange}
//           required
//         />

//         <label>Role:</label>
//         <select name="role" value={formData.role} onChange={handleChange} required>
//           <option value="">Select Role</option>
//           <option value="admin">Admin</option>
//           <option value="hr">HR</option>
//           <option value="manager">Manager</option>
//           <option value="accountant">Accountant</option>
//         </select>

//         <label>Password:</label>
//         <input
//           type="password"
//           name="password"
//           value={formData.password}
//           onChange={handleChange}
//           required
//           minLength={8}
//         />

//         <label>Businesses:</label>
//         <div style={styles.bizList}>
//           {businesses.map((biz) => (
//             <label key={biz.id} style={styles.bizItem}>
//               <input
//                 type="checkbox"
//                 checked={selectedBizIds.includes(biz.id)}
//                 onChange={() => toggleBiz(biz.id)}
//               />
//               <span>{biz.name}</span>
//             </label>
//           ))}
//           {businesses.length === 0 && (
//             <div style={{ color: "#666" }}>No businesses found.</div>
//           )}
//         </div>

//         <button type="submit">Add User</button>
//       </form>

//       {/* ------- New: list existing business users ------- */}
//       <div style={{ marginTop: 24 }}>
//         <h3>Existing Business Users</h3>

//         {/* Filter by business */}
//         <div style={{ marginBottom: 8 }}>
//           <label style={{ marginRight: 8 }}>Filter by Business:</label>
//           <select
//             value={filterBizId}
//             onChange={(e) => setFilterBizId(e.target.value)}
//           >
//             <option value="">All</option>
//             {businesses.map((b) => (
//               <option key={b.id} value={b.id}>
//                 {b.name}
//               </option>
//             ))}
//           </select>
//           <button
//             type="button"
//             onClick={() => fetchBusinessUsers(filterBizId)}
//             style={{ marginLeft: 8 }}
//           >
//             Refresh
//           </button>
//         </div>

//         {usersError && <p style={styles.error}>{usersError}</p>}
//         {usersLoading && <p>Loading...</p>}

//         {!usersLoading && businessUsers.length === 0 && (
//           <p style={{ color: "#666" }}>No business users found.</p>
//         )}

//         <div style={styles.userList}>
//           {businessUsers.map((u) => (
//             <div key={u.id} style={styles.userCard}>
//               <div style={{ fontWeight: "bold" }}>
//                 {u.first_name} {u.last_name}
//               </div>
//               <div>{u.email}</div>
//               <div>{u.phone}</div>
//               <div>Role: {u.role}</div>
//               <div style={{ fontSize: 12, color: "#555" }}>
//                 {u.business_names && u.business_names.length
//                   ? `Businesses: ${u.business_names.join(", ")}`
//                   : "Businesses: -"}
//               </div>
//             </div>
//           ))}
//         </div>
//       </div>
//     </div>
//   );
// };

// // Inline Styles (kept your original base styles)
// const styles = {
//   container: {
//     maxWidth: "400px",
//     margin: "50px auto",
//     padding: "20px",
//     fontFamily: "Arial, sans-serif",
//     border: "1px solid #ddd",
//     borderRadius: "8px",
//     boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.1)",
//     backgroundColor: "#fff",
//   },
//   form: {
//     display: "flex",
//     flexDirection: "column",
//   },
//   error: {
//     color: "red",
//     fontSize: "14px",
//     marginBottom: "10px",
//   },
//   success: {
//     color: "green",
//     fontSize: "14px",
//     marginBottom: "10px",
//   },
//   bizList: {
//     display: "flex",
//     flexDirection: "column",
//     gap: "6px",
//     marginBottom: "8px",
//   },
//   bizItem: {
//     display: "flex",
//     alignItems: "center",
//     gap: "8px",
//   },
//   // New styles for the list section
//   userList: {
//     display: "flex",
//     flexDirection: "column",
//     gap: "8px",
//     marginTop: "8px",
//   },
//   userCard: {
//     border: "1px solid #eee",
//     borderRadius: "6px",
//     padding: "8px",
//     background: "#fafafa",
//   },
// };

// export default AddUserPage;




// import React, { useState, useEffect } from "react";
// import api from "../services/api"; // Your API service

// const AddUserPage = () => {
//   const [formData, setFormData] = useState({
//     first_name: "",
//     last_name: "",
//     email: "",
//     phone: "",
//     role: "",
//     password: "",
//   });

//   const [businesses, setBusinesses] = useState([]);
//   const [selectedBizIds, setSelectedBizIds] = useState([]);

//   const [error, setError] = useState(null);
//   const [success, setSuccess] = useState(null);

//   // Load businesses for the authenticated user
//   useEffect(() => {
//     const load = async () => {
//       try {
//         const res = await api.get("/businesses"); // uses authenticateToken on backend
//         setBusinesses(res.data || []);
//       } catch {
//         setError("Failed to load businesses.");
//       }
//     };
//     load();
//   }, []);

//   const handleChange = (e) => {
//     setFormData({ ...formData, [e.target.name]: e.target.value });
//   };

//   const toggleBiz = (id) => {
//     setSelectedBizIds((prev) =>
//       prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
//     );
//   };

//   const handleSubmit = async (e) => {
//     e.preventDefault();
//     setError(null);
//     setSuccess(null);

//     // Basic checks (keep it simple like your original style)
//     const { first_name, last_name, email, phone, role, password } = formData;
//     if (!first_name || !last_name || !email || !phone || !role || !password) {
//       return setError("All fields are required.");
//     }
//     if (selectedBizIds.length === 0) {
//       return setError("Select at least one business.");
//     }
//     if (role === "manager" && selectedBizIds.length !== 1) {
//       return setError("Managers can be assigned to exactly one business.");
//     }

//     try {
//       // New bulk endpoint: server gets user_id from token, we just send payload
//       const payload = {
//         first_name,
//         last_name,
//         email: email.toLowerCase(),
//         phone,
//         password,
//         role, // 'admin' | 'hr' | 'manager' | 'accountant'
//         business_ids: selectedBizIds,
//       };

//       const response = await api.post("/business-user-add-bulk", payload);
//       setSuccess(response?.data || "Business user created and linked.");

//       // Reset
//       setFormData({
//         first_name: "",
//         last_name: "",
//         email: "",
//         phone: "",
//         role: "",
//         password: "",
//       });
//       setSelectedBizIds([]);
//     } catch (err) {
//       console.error("Error adding business user:", err);
//       setError(err?.response?.data || "Failed to add business user.");
//     }
//   };

//   return (
//     <div style={styles.container}>
//       <h2>Add New User</h2>

//       {success && <p style={styles.success}>{success}</p>}
//       {error && <p style={styles.error}>{error}</p>}

//       <form onSubmit={handleSubmit} style={styles.form}>
//         <label>First Name:</label>
//         <input
//           type="text"
//           name="first_name"
//           value={formData.first_name}
//           onChange={handleChange}
//           required
//         />

//         <label>Last Name:</label>
//         <input
//           type="text"
//           name="last_name"
//           value={formData.last_name}
//           onChange={handleChange}
//           required
//         />

//         <label>Email:</label>
//         <input
//           type="email"
//           name="email"
//           value={formData.email}
//           onChange={handleChange}
//           required
//         />

//         <label>Phone:</label>
//         <input
//           type="tel"
//           name="phone"
//           value={formData.phone}
//           onChange={handleChange}
//           required
//         />

//         <label>Role:</label>
//         <select name="role" value={formData.role} onChange={handleChange} required>
//           <option value="">Select Role</option>
//           <option value="admin">Admin</option>
//           <option value="hr">HR</option>
//           <option value="manager">Manager</option>
//           <option value="accountant">Accountant</option>
//         </select>

//         <label>Password:</label>
//         <input
//           type="password"
//           name="password"
//           value={formData.password}
//           onChange={handleChange}
//           required
//           minLength={8}
//         />

//         <label>Businesses:</label>
//         <div style={styles.bizList}>
//           {businesses.map((biz) => (
//             <label key={biz.id} style={styles.bizItem}>
//               <input
//                 type="checkbox"
//                 checked={selectedBizIds.includes(biz.id)}
//                 onChange={() => toggleBiz(biz.id)}
//               />
//               <span>{biz.name}</span>
//             </label>
//           ))}
//           {businesses.length === 0 && (
//             <div style={{ color: "#666" }}>No businesses found.</div>
//           )}
//         </div>

//         <button type="submit">Add User</button>
//       </form>
//     </div>
//   );
// };

// // Inline Styles (kept same base style as your original)
// const styles = {
//   container: {
//     maxWidth: "400px",
//     margin: "50px auto",
//     padding: "20px",
//     fontFamily: "Arial, sans-serif",
//     border: "1px solid #ddd",
//     borderRadius: "8px",
//     boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.1)",
//     backgroundColor: "#fff",
//   },
//   form: {
//     display: "flex",
//     flexDirection: "column",
//   },
//   error: {
//     color: "red",
//     fontSize: "14px",
//     marginBottom: "10px",
//   },
//   success: {
//     color: "green",
//     fontSize: "14px",
//     marginBottom: "10px",
//   },
//   bizList: {
//     display: "flex",
//     flexDirection: "column",
//     gap: "6px",
//     marginBottom: "8px",
//   },
//   bizItem: {
//     display: "flex",
//     alignItems: "center",
//     gap: "8px",
//   },
// };

// export default AddUserPage;



// import React, { useState } from "react";
// import api from "../services/api"; // Your API service

// const AddUserPage = () => {
//     const [formData, setFormData] = useState({
//         first_name: "",
//         last_name: "",
//         email: "",
//         phone: "",
//         role: "",
//     });

//     const [error, setError] = useState(null);
//     const [success, setSuccess] = useState(null);

//     const handleChange = (e) => {
//         setFormData({ ...formData, [e.target.name]: e.target.value });
//     };

//     const handleSubmit = async (e) => {
//         e.preventDefault();
//         setError(null);
//         setSuccess(null);

//         try {
//             const response = await api.post("/user-add", formData);
//             setSuccess(response.data.message);
//             setFormData({
//                 first_name: "",
//                 last_name: "",
//                 email: "",
//                 phone: "",
//                 role: "",
//             });
//         } catch (err) {
//             console.error("Error adding user:", err);
//             setError("Failed to add user.");
//         }
//     };

//     return (
//         <div style={styles.container}>
//             <h2>Add New User</h2>

//             {success && <p style={styles.success}>{success}</p>}
//             {error && <p style={styles.error}>{error}</p>}

//             <form onSubmit={handleSubmit} style={styles.form}>
//                 <label>First Name:</label>
//                 <input type="text" name="first_name" value={formData.first_name} onChange={handleChange} required />

//                 <label>Last Name:</label>
//                 <input type="text" name="last_name" value={formData.last_name} onChange={handleChange} required />

//                 <label>Email:</label>
//                 <input type="email" name="email" value={formData.email} onChange={handleChange} required />

//                 <label>Phone:</label>
//                 <input type="tel" name="phone" value={formData.phone} onChange={handleChange} required />

//                 <label>Role:</label>
//                     <select name="role" value={formData.role} onChange={handleChange} required>
//                     <option value="">Select Role</option>
//                     <option value="admin">Admin</option>
//                     <option value="hr">HR</option>
//                     <option value="manager">Manager</option>
//                     <option value="accountant">Accountant</option>
//                     </select>


//                 <button type="submit">Add User</button>
//             </form>
//         </div>
//     );
// };

// // Inline Styles
// const styles = {
//     container: {
//         maxWidth: "400px",
//         margin: "50px auto",
//         padding: "20px",
//         fontFamily: "Arial, sans-serif",
//         border: "1px solid #ddd",
//         borderRadius: "8px",
//         boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.1)",
//         backgroundColor: "#fff",
//     },
//     form: {
//         display: "flex",
//         flexDirection: "column",
//     },
//     error: {
//         color: "red",
//         fontSize: "14px",
//         marginBottom: "10px",
//     },
//     success: {
//         color: "green",
//         fontSize: "14px",
//         marginBottom: "10px",
//     },
// };

// export default AddUserPage;
